import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { BOSSES } from '../bosses'
import type { EventBus } from '../events'
import { T } from '../tunables'

const spec = BOSSES.chain
const C = T.boss.chain

function enterDormant(world: GameWorld, boss: Entity): void {
  const chain = boss.chain!
  const b = boss.boss!
  const N = chain.initialSnapshot.length

  // Rebuild full segment array — re-create any segments that died mid-fight.
  const aliveByIndex = new Map<number, Entity>()
  for (const s of chain.segments) aliveByIndex.set(s.chainSegment!.index, s)

  const newSegments: Entity[] = []
  for (let i = 0; i < N; i++) {
    const snap = chain.initialSnapshot[i]
    let seg = aliveByIndex.get(i)
    if (!seg) {
      const hueOffset = N > 1 ? i / (N - 1) : 0
      seg = world.add({
        position: { x: snap.x, y: snap.y },
        velocity: { x: 0, y: 0 },
        wobble: { phase: Math.random() * Math.PI * 2, freq: 0.6, amp: 0.04 },
        renderable: {
          shape: 'chainSegment',
          radius: spec.segmentRadius,
          tint: [0.55 + hueOffset * 0.45, 0.75 + hueOffset * 0.2, 1.0],
        },
        health: { current: spec.segmentHp, max: spec.segmentHp },
        chainSegment: { parent: boss, index: i, hueOffset, popTimer: 0 },
      })
    } else {
      seg.position.x = snap.x
      seg.position.y = snap.y
      if (seg.velocity) { seg.velocity.x = 0; seg.velocity.y = 0 }
      seg.health!.current = spec.segmentHp
    }
    newSegments.push(seg)
  }
  chain.segments = newSegments

  const totalHp = N * spec.segmentHp
  boss.health!.current = totalHp
  boss.position.x = newSegments[0].position.x
  boss.position.y = newSegments[0].position.y

  b.state = 'dormant'
  b.stateTimer = 0
  b.phase = 1
  b.outOfRangeTimer = 0
}

// Runs AFTER predatorAISystem (which runs after movementSystem).
// Head has already been moved this frame by movementSystem; the rope
// constraint here corrects segments[1..N] relative to the moved head,
// so positions are consistent at render time.
export function chainBossAISystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  const bossList = [...world.with('boss', 'chain')]
  if (bossList.length === 0) return
  const boss = bossList[0]
  const b = boss.boss!
  const chain = boss.chain!
  const segments = chain.segments
  if (segments.length === 0) return

  const head = segments[0]

  // Phase 2 check (runs before state machine so multipliers apply this frame).
  const initialCount = chain.initialSnapshot.length
  if (b.phase === 1 && segments.length < initialCount * spec.phase2Threshold) {
    b.phase = 2
    events.emit({ type: 'boss.phase2', boss })
  }

  const phase2 = b.phase === 2
  const intervalMult = phase2 ? 0.65 : 1.0
  const speedMult    = phase2 ? 1.25 : 1.0

  // Re-curl check: player has been out of range for too long.
  if (b.state !== 'dormant') {
    const dx = player.position.x - head.position.x
    const dy = player.position.y - head.position.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist > b.awakeRange) {
      b.outOfRangeTimer += dt
      if (b.outOfRangeTimer >= b.recurlGrace) {
        enterDormant(world, boss)
        return
      }
    } else {
      b.outOfRangeTimer = 0
    }
  }

  // ── State machine ──────────────────────────────────────────────────────────

  let headVx = head.velocity?.x ?? 0
  let headVy = head.velocity?.y ?? 0

  const pdx = player.position.x - head.position.x
  const pdy = player.position.y - head.position.y
  const pDist = Math.sqrt(pdx * pdx + pdy * pdy)

  if (b.state === 'dormant') {
    // Snap all segments to their coil positions each frame (invulnerable hold).
    for (const seg of segments) {
      const snap = chain.initialSnapshot[seg.chainSegment!.index]
      seg.position.x = snap.x
      seg.position.y = snap.y
      if (seg.velocity) { seg.velocity.x = 0; seg.velocity.y = 0 }
    }
    boss.position.x = head.position.x
    boss.position.y = head.position.y

    // Wake: player in range AND DNA gate met.
    if (pDist <= b.awakeRange && (player.dna ?? 0) >= spec.dnaGate) {
      b.state = 'uncurling'
      b.stateTimer = C.uncurlDuration
      events.emit({ type: 'boss.wake', boss })
    }
    return
  }

  if (b.state === 'uncurling') {
    b.stateTimer -= dt
    // Head drifts slowly toward player; rope pulls segments out of the coil naturally.
    if (pDist > 0.01) {
      const targetVx = (pdx / pDist) * C.idleSpeed * 0.5
      const targetVy = (pdy / pDist) * C.idleSpeed * 0.5
      headVx += (targetVx - headVx) * Math.min(1, 2 * dt)
      headVy += (targetVy - headVy) * Math.min(1, 2 * dt)
    }
    if (b.stateTimer <= 0) {
      b.state = 'idle'
      b.stateTimer = C.windupCooldownBase * intervalMult
    }

  } else if (b.state === 'idle') {
    // Chase player.
    if (pDist > 0.01) {
      const targetVx = (pdx / pDist) * C.idleSpeed * speedMult
      const targetVy = (pdy / pDist) * C.idleSpeed * speedMult
      headVx += (targetVx - headVx) * Math.min(1, 4 * dt)
      headVy += (targetVy - headVy) * Math.min(1, 4 * dt)
    }
    // Attack picker when cooldown expires.
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      const chainSpec = spec.chain!
      if (pDist < chainSpec.whipRange * 1.5) {
        b.state = 'coil'
        b.stateTimer = (chainSpec.coilWindup + chainSpec.coilHold) * intervalMult
        chain.coilCenter.x = player.position.x
        chain.coilCenter.y = player.position.y
      } else {
        b.state = 'windup'
        b.stateTimer = chainSpec.whipWindup * intervalMult
        if (pDist > 0) {
          chain.whipDir.x = pdx / pDist
          chain.whipDir.y = pdy / pDist
        }
      }
    }

  } else if (b.state === 'windup') {
    // Telegraph: freeze the head.
    const damp = Math.max(0, 1 - 6 * dt)
    headVx *= damp
    headVy *= damp
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      b.state = 'strike'
      b.stateTimer = C.strikeDuration
      headVx = chain.whipDir.x * C.strikeSpeed * speedMult
      headVy = chain.whipDir.y * C.strikeSpeed * speedMult
    }

  } else if (b.state === 'strike') {
    headVx = chain.whipDir.x * C.strikeSpeed * speedMult
    headVy = chain.whipDir.y * C.strikeSpeed * speedMult
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      b.state = 'recover'
      b.stateTimer = spec.chain!.whipRecovery * intervalMult
    }

  } else if (b.state === 'recover') {
    const damp = Math.max(0, 1 - 5 * dt)
    headVx *= damp
    headVy *= damp
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      b.state = 'idle'
      b.stateTimer = C.windupCooldownBase * intervalMult
    }

  } else if (b.state === 'coil') {
    // Head orbits coilCenter at coilRadius.
    const cc = chain.coilCenter
    const toCX = cc.x - head.position.x
    const toCY = cc.y - head.position.y
    const distToC = Math.sqrt(toCX * toCX + toCY * toCY)
    const coilR = spec.chain!.coilRadius

    if (distToC > 0.01) {
      const nx = toCX / distToC
      const ny = toCY / distToC
      // Clockwise tangent: [ny, -nx]
      const tangX =  ny * C.coilAngularSpeed * coilR * speedMult
      const tangY = -nx * C.coilAngularSpeed * coilR * speedMult
      // Radial correction toward target radius
      const radialErr = distToC - coilR
      headVx = tangX + nx * radialErr * 5
      headVy = tangY + ny * radialErr * 5
    }
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      b.state = 'recoil'
      b.stateTimer = C.recoilDuration
    }

  } else if (b.state === 'recoil') {
    // Head unwinds away from coilCenter.
    const cc = chain.coilCenter
    const awayX = head.position.x - cc.x
    const awayY = head.position.y - cc.y
    const awayDist = Math.sqrt(awayX * awayX + awayY * awayY)
    if (awayDist > 0.01) {
      headVx = (awayX / awayDist) * C.recoilSpeed * speedMult
      headVy = (awayY / awayDist) * C.recoilSpeed * speedMult
    }
    b.stateTimer -= dt
    if (b.stateTimer <= 0) {
      b.state = 'idle'
      b.stateTimer = C.windupCooldownBase * intervalMult
    }
  }

  // Write head velocity back (movementSystem will apply it next frame).
  if (head.velocity) {
    head.velocity.x = headVx
    head.velocity.y = headVy
  }

  // ── Rope constraint ────────────────────────────────────────────────────────
  // Head has already been moved by movementSystem this frame; walk the chain
  // head-first and clamp each segment within linkDistance of its predecessor.
  const linkDist = spec.linkDistance
  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1]
    const curr = segments[i]
    const dx = curr.position.x - prev.position.x
    const dy = curr.position.y - prev.position.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist > linkDist && dist > 0) {
      const s = linkDist / dist
      curr.position.x = prev.position.x + dx * s
      curr.position.y = prev.position.y + dy * s
    }
  }

  // Sync parent entity position to head (used by camera / distance checks).
  boss.position.x = head.position.x
  boss.position.y = head.position.y
}
